import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const GRAD = 'linear-gradient(100deg,#3B3CC4 0%,#7C2BD0 30%,#C81E73 55%,#E51E50 72%,#F26A2C 100%)';

// Inline LN brand badge — used as a fallback if /logo.png is absent
function LnBadge({ size = 60 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ borderRadius: 16, boxShadow: '0 6px 20px rgba(0,0,0,0.4)' }}>
      <rect width="100" height="100" rx="22" fill="#0B0D14" />
      <circle cx="50" cy="42" r="26" fill="#fff" />
      <text x="50" y="52" fontFamily="'Hanken Grotesk',sans-serif" fontSize="26" fontWeight="800" fill="#7C2BD0" textAnchor="middle">LN</text>
    </svg>
  );
}

// Brand logo: prefers /logo.png (drop your file in frontend/public/), falls back to the LN badge
function BrandLogo({ size = 60 }) {
  const [err, setErr] = useState(false);
  if (err) return <LnBadge size={size} />;
  return (
    <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="LavanAI" width={size} height={size}
      onError={() => setErr(true)}
      style={{ borderRadius: 16, objectFit: 'cover', boxShadow: '0 6px 20px rgba(0,0,0,0.4)', display: 'block', margin: '0 auto 14px' }} />
  );
}

const Eye = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const EyeOff = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" /><path d="M1 1l22 22M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </svg>
);
const Arrow = () => (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

export default function Login() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ username: '', password: '', otp: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  async function handleCredentials(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: form.username, password: form.password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Login failed');

      // DEV MODE: backend returns token directly (no OTP step)
      if (data.token) {
        localStorage.setItem('ic_token', data.token);
        localStorage.setItem('ic_user', JSON.stringify({
          userId: data.userId,
          username: data.username,
          email: data.email,
        }));
        navigate('/dashboard');
        return;
      }

      // PRODUCTION: OTP step
      setMaskedEmail(data.email);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleOTP(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: form.username, otp: form.otp }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'OTP verification failed');
      localStorage.setItem('ic_token', data.token);
      localStorage.setItem('ic_user', JSON.stringify({
        userId: data.userId,
        username: data.username,
        email: data.email,
      }));
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="iclg" style={{ '--grad': GRAD }}>
      <style>{CSS}</style>
      <div className="iclg-grid" />
      <div className="iclg-glow g1" />
      <div className="iclg-glow g2" />

      <div className="iclg-card">
        <div className="iclg-brand">
          <BrandLogo />
          <h1>Lavan<span style={{ background: GRAD, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>AI</span></h1>
          <p>AI-powered interview copilot</p>
        </div>

        {error && <div className="iclg-err">{error}</div>}

        {step === 1 ? (
          <form onSubmit={handleCredentials}>
            <label className="iclg-lab">Username</label>
            <input className="iclg-f" type="text" name="username"
              value={form.username} onChange={handleChange}
              placeholder="Enter username" autoComplete="username" required autoFocus />

            <label className="iclg-lab">Password</label>
            <div className="iclg-fwrap">
              <input className="iclg-f" type={showPassword ? 'text' : 'password'} name="password"
                value={form.password} onChange={handleChange}
                placeholder="Enter password" autoComplete="current-password" required />
              <button type="button" className="iclg-eye" aria-label="Toggle password"
                onClick={() => setShowPassword(v => !v)}>
                {showPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>

            <button className="iclg-btn" type="submit" disabled={loading}>
              {loading ? <span className="iclg-spin" /> : <>Sign in <Arrow /></>}
            </button>
            <div className="iclg-div">secured</div>
            <p className="iclg-foot">LavanAI Interview Copilot<br />Real-time prompts · ATS-aligned · C2C IT consulting</p>
          </form>
        ) : (
          <form onSubmit={handleOTP}>
            <p className="iclg-otpinfo">OTP sent to <b>{maskedEmail}</b></p>
            <p className="iclg-otptimer">⏱ Expires in 3 minutes</p>
            <input className="iclg-f iclg-otp" type="text" name="otp" inputMode="numeric"
              value={form.otp} onChange={handleChange}
              placeholder="000000" maxLength={6} required autoFocus />
            <button className="iclg-btn" type="submit" disabled={loading}>
              {loading ? <span className="iclg-spin" /> : 'Verify OTP'}
            </button>
            <button type="button" className="iclg-back"
              onClick={() => { setStep(1); setError(''); setForm({ ...form, otp: '' }); }}>
              ← Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const CSS = `
.iclg{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;
  background:#0B0D14;overflow:hidden;font-family:'Hanken Grotesk',sans-serif;}
.iclg-grid{position:absolute;inset:0;
  background-image:linear-gradient(rgba(124,43,208,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(124,43,208,0.05) 1px,transparent 1px);
  background-size:48px 48px;
  -webkit-mask-image:radial-gradient(circle at 50% 42%,#000 0%,transparent 78%);
  mask-image:radial-gradient(circle at 50% 42%,#000 0%,transparent 78%);}
.iclg-glow{position:absolute;border-radius:50%;filter:blur(90px);opacity:.5;}
.iclg-glow.g1{width:520px;height:520px;background:radial-gradient(circle,#5B2BD6,transparent 65%);top:-15%;left:-10%;}
.iclg-glow.g2{width:480px;height:480px;background:radial-gradient(circle,#E51E50,transparent 65%);bottom:-18%;right:-8%;opacity:.4;}
.iclg-card{position:relative;z-index:1;width:100%;max-width:400px;margin:20px;background:#151823;
  border:1px solid rgba(255,255,255,0.08);border-radius:20px;padding:38px 34px 32px;
  box-shadow:0 24px 60px rgba(0,0,0,0.55);}
.iclg-brand{text-align:center;margin-bottom:26px;}
.iclg-brand svg{margin:0 auto 14px;display:block;}
.iclg-brand h1{font-size:24px;font-weight:800;letter-spacing:-0.5px;color:#fff;}
.iclg-brand p{font-size:12.5px;color:#8892A4;margin-top:5px;}
.iclg-lab{display:block;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.6px;color:#8892A4;margin-bottom:7px;}
.iclg-f{width:100%;background:#0F1320;border:1.5px solid rgba(255,255,255,0.1);border-radius:9px;
  padding:12px 14px;font-size:14px;color:#F0F2F8;outline:none;margin-bottom:15px;transition:all .18s;
  font-family:inherit;box-sizing:border-box;}
.iclg-f:focus{border-color:#7C2BD0;box-shadow:0 0 0 3px rgba(124,43,208,0.18);}
.iclg-fwrap{position:relative;}
.iclg-fwrap .iclg-f{padding-right:44px;}
.iclg-eye{position:absolute;right:10px;top:10px;background:none;border:none;color:#8892A4;cursor:pointer;padding:5px;display:flex;border-radius:6px;transition:color .15s;}
.iclg-eye:hover{color:#C4B5FD;}
.iclg-otp{text-align:center;font-size:26px;letter-spacing:10px;font-weight:700;}
.iclg-btn{width:100%;margin-top:6px;padding:13px;background:var(--grad);color:#fff;font-size:15px;font-weight:700;
  border:none;border-radius:9px;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;
  white-space:nowrap;box-shadow:0 8px 22px rgba(124,43,208,0.38);transition:transform .18s;font-family:inherit;}
.iclg-btn:hover{transform:translateY(-2px);}
.iclg-btn:disabled{opacity:.8;cursor:default;transform:none;}
.iclg-div{display:flex;align-items:center;gap:12px;margin:20px 0 14px;color:#5b6478;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;}
.iclg-div::before,.iclg-div::after{content:'';flex:1;height:1px;background:rgba(255,255,255,0.1);}
.iclg-foot{text-align:center;font-size:12px;color:#8892A4;line-height:1.6;margin-top:4px;}
.iclg-otpinfo{color:#C9D1E0;text-align:center;margin-bottom:4px;font-size:14px;}
.iclg-otpinfo b{color:#C4B5FD;}
.iclg-otptimer{color:#F59E0B;text-align:center;margin-bottom:18px;font-size:12px;}
.iclg-back{width:100%;margin-top:10px;padding:11px;background:transparent;border:1px solid rgba(255,255,255,0.12);
  border-radius:9px;color:#8892A4;font-size:14px;cursor:pointer;font-family:inherit;}
.iclg-err{background:rgba(229,30,80,0.12);border:1px solid rgba(229,30,80,0.5);color:#FF8FA8;
  padding:10px 14px;border-radius:9px;font-size:13px;margin-bottom:16px;text-align:center;}
.iclg-spin{width:17px;height:17px;border:2.5px solid rgba(255,255,255,0.35);border-top-color:#fff;border-radius:50%;display:inline-block;animation:iclgsp .6s linear infinite;}
@keyframes iclgsp{to{transform:rotate(360deg);}}
`;
